/* ====================================================================
 * bs-upsell-test
 * --------------------------------------------------------------------
 * Self-contained in-cart upsell behavior. The only thing exposed on
 * window is `window.bsUpsellTest` (idempotent helpers).
 *
 * Visibility: the snippet renders with inline `style="display:none;"`
 * and class `.bs-upsell-test`. The A/B test framework reveals the
 * module by injecting:
 *
 *     .bs-upsell-test { display: block !important; }
 *
 * (The `!important` is required to override the inline style.) Console
 * helpers `window.bsUpsellTest.show()` / `.hide()` are also available
 * for manual testing.
 *
 * Does NOT:
 *   - touch Frenzy's `.frenzy_*` nodes
 *   - replace native cart events
 *   - register globals other than `window.bsUpsellTest`
 *
 * Does:
 *   - intercept clicks on `[data-bs-upsell-add]` (capture-phase document
 *     listener so it survives cart-drawer re-renders)
 *   - POST to /cart/add.js Dawn-style with `sections=...` so the
 *     response includes sectionally-rendered HTML
 *   - call `<cart-items>.renderContents(response)` to swap the drawer
 *     DOM in place (no `viewDrawer()` toggle — drawer is already open)
 * ==================================================================== */
(function () {
  'use strict';

  if (window.bsUpsellTest && window.bsUpsellTest.__initialized) {
    return;
  }

  var api = {
    __initialized: true,
    version: '1.1.0',

    /** Manually reveal the module (overrides the inline display:none). */
    show: function (root) {
      var el = root || document.querySelector('[data-bs-upsell-root]');
      if (el) el.style.display = '';
    },

    /** Manually hide the module again. */
    hide: function (root) {
      var el = root || document.querySelector('[data-bs-upsell-root]');
      if (el) el.style.display = 'none';
    }
  };

  function setStatus(root, message) {
    var status = root && root.querySelector('[data-bs-upsell-status]');
    if (status) status.textContent = message || '';
  }

  function handleClick(event) {
    var btn = event.target.closest && event.target.closest('[data-bs-upsell-add]');
    if (!btn) return;

    // +Add button is type="button", but stop propagation so any outer
    // cart-form listeners (Dawn cart-items / Frenzy) don't react.
    event.preventDefault();
    event.stopPropagation();

    var form = btn.closest('[data-bs-upsell-form]');
    if (!form) return;

    var root = form.closest('[data-bs-upsell-root]');
    var card = form.closest('[data-bs-upsell-card]');
    var button = btn;
    var productUrl = form.getAttribute('data-bs-upsell-url');

    // Resolve variant id: prefer the customer's choice in the <select>, fall
    // back to the product's selected_or_first_available_variant from Liquid.
    var variantId = form.getAttribute('data-bs-upsell-variant-id');
    var selectEl = form.querySelector('[data-bs-upsell-variant-select]');
    if (selectEl && selectEl.value) {
      variantId = selectEl.value;
    }

    // No variant id at all → fall back to the PDP so the user isn't
    // left with a silently-broken button.
    if (!variantId) {
      if (productUrl) window.location.href = productUrl;
      return;
    }

    if (button) {
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    }
    setStatus(root, 'Adding…');

    // Same payload shape Bombshell's Frenzy quickview uses
    // (assets/global.js ~line 1304). Passing `sections` as a
    // comma-separated string makes Shopify include sectionally-rendered
    // HTML in the response, which Dawn's <cart-items>.renderContents()
    // then swaps into the drawer.
    var body = JSON.stringify({
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: 'cart-drawer,cart-icon-bubble,cart-live-region-text,cart_footer',
      sections_url: window.location.pathname
    });

    var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    fetch(routeRoot + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: body
    })
      .then(function (res) {
        return res.json().then(function (j) { return { ok: res.ok, status: res.status, body: j }; });
      })
      .then(function (result) {
        if (!result.ok || result.body.status === 422 || result.body.status === 'bad_request') {
          var msg = (result.body && (result.body.description || result.body.message)) || ('HTTP ' + result.status);
          throw new Error(msg);
        }

        if (card) card.setAttribute('data-bs-upsell-state', 'added');
        setStatus(root, '');

        // Tell Dawn to swap drawer/icon-bubble/etc HTML. Mirrors
        // Frenzy modal pattern (assets/global.js ~1328). Do NOT call
        // viewDrawer() — the drawer is already open and viewDrawer()
        // toggles the close-drawer class.
        var cartItems = document.querySelector('cart-items');
        if (cartItems && typeof cartItems.renderContents === 'function') {
          cartItems.renderContents(result.body);
        }

        // Best-effort: nudge any listeners (wishlist, analytics, etc.).
        document.documentElement.dispatchEvent(
          new CustomEvent('bs:upsell:added', { bubbles: true, detail: result.body })
        );
        if (window.PUB_SUB_EVENTS && typeof window.publish === 'function') {
          try { window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'bs-upsell-test', cartData: result.body }); } catch (e) {}
        }
      })
      .catch(function (err) {
        setStatus(root, (err && err.message) ? err.message : 'Could not add item.');
        if (button) {
          button.removeAttribute('aria-busy');
          button.disabled = false;
        }
      });
  }

  // Single delegated listener — survives cart re-renders since it's on
  // document. Use click (not submit) because the +Add button is plain
  // <button type="button">; the cart drawer is wrapped in <form id="cart">
  // and browsers flatten nested forms.
  document.addEventListener('click', handleClick, true);

  window.bsUpsellTest = api;
})();
